"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import { track } from "@/lib/analytics/tracker";
import type { SignedIn } from "@/lib/auth/sign-in/result";
import { signInWithPassword } from "../sign-in-actions";
import { SocialSignIn } from "../social-sign-in";
import { AuthField } from "./auth-field";
import { AuthError, AuthNotice } from "./auth-messages";
import { ForgotPasswordButton, useForgotPassword } from "./forgot-password";
import { useAuthCompletion } from "./use-auth-completion";
import { useSendCode } from "./email-code-step";

export interface LogInStepProps {
  entryPoint: AuthEntryPoint;
  initialEmail?: string;
  quoteClaimToken?: string;
  notice?: string;
  showProviders: boolean;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
  onNeedsCode: () => void;
  onUseCode: (email: string) => void;
  onCodeSent: (email: string) => void;
  onSignUp: (email: string) => void;
}

export function LogInStep(props: LogInStepProps) {
  const [email, setEmail] = useState(props.initialEmail ?? "");
  const [password, setPassword] = useState("");
  const forgot = useForgotPassword();
  const completion = useAuthCompletion({
    entryPoint: props.entryPoint,
    surface: "password",
    method: "password",
    onSignedIn: props.onSignedIn,
    onNeedsCode: props.onNeedsCode,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    track("auth.started", { entry_point: props.entryPoint, method: "password" });
    void completion.run(() => signInWithPassword({ email, password, quoteClaimToken: props.quoteClaimToken }));
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <AuthNotice message={props.notice} />
      <AuthField label="Email" type="email" autoComplete="email" autoFocus value={email} onChange={setEmail} />
      <AuthField
        label="Password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
        labelAction={<ForgotPasswordButton isSending={forgot.isSending} onClick={() => void forgot.request(email)} />}
      />
      <AuthNotice message={forgot.message ?? undefined} />
      {completion.offerEmailedLink ? (
        <EmailedLinkOffer entryPoint={props.entryPoint} email={email} isBusy={completion.isBusy} onCodeSent={props.onCodeSent} />
      ) : (
        <LogInActions error={completion.error} isBusy={completion.isBusy} onUseCode={() => props.onUseCode(email)} />
      )}
      {props.showProviders ? <SocialSignIn /> : null}
      <p className="text-center text-sm text-muted-foreground">
        New to Repackify?{" "}
        <button type="button" className="font-medium text-foreground underline disabled:opacity-50" disabled={completion.isBusy} onClick={() => props.onSignUp(email)}>
          Create an account
        </button>
      </p>
    </form>
  );
}

function LogInActions(props: { error: string | null; isBusy: boolean; onUseCode: () => void }) {
  return (
    <>
      <AuthError message={props.error} />
      <Button type="submit" variant="primary" className="w-full" disabled={props.isBusy}>
        {props.isBusy ? "Logging in…" : "Log in"}
      </Button>
      <Button type="button" variant="outline" className="w-full" disabled={props.isBusy} onClick={props.onUseCode}>
        Email me a code instead
      </Button>
    </>
  );
}

function EmailedLinkOffer(props: {
  entryPoint: AuthEntryPoint;
  email: string;
  isBusy: boolean;
  onCodeSent: (email: string) => void;
}) {
  const sending = useSendCode(props.entryPoint);
  const send = async () => {
    track("auth.emailed_link_requested", { entry_point: props.entryPoint });
    const sentTo = await sending.send(props.email);
    if (sentTo) props.onCodeSent(sentTo);
  };
  return (
    <>
      <AuthError message={sending.error ?? "We couldn't log you in just now. We can email you a link that signs you in instead."} />
      <Button type="button" variant="primary" className="w-full" disabled={sending.isSending} onClick={() => void send()}>
        {sending.isSending ? "Sending your link…" : "Email me a sign-in link"}
      </Button>
      <Button type="submit" variant="outline" className="w-full" disabled={props.isBusy || sending.isSending}>
        {props.isBusy ? "Logging in…" : "Try the password again"}
      </Button>
    </>
  );
}
